/* eslint-disable max-lines */
import { PDFDocument, rgb, StandardFonts, PDFFont, PDFCheckBox, PDFDropdown, PDFTextField } from 'pdf-lib';
import moment from 'moment';
import { FormIT2104Data } from '../views/pages/NewHire/FormIT2104Modal';

const drawText = (
  page: any,
  text: string | undefined | null,
  x: number,
  y: number,
  font: PDFFont,
  size: number,
  color = rgb(0, 0, 0),
) => {
  if (text === undefined || text === null || text === '') return;
  page.drawText(String(text), { x, y, font, size, color });
};

const drawCheckboxMark = (
  page: any,
  x: number,
  y: number,
  font: PDFFont,
  size = 10,
  color = rgb(0, 0, 0),
  mark = 'X',
) => {
  page.drawText(mark, {
    x: x + 2,
    y: y + 1,
    font,
    size,
    color,
  });
};

const readOnlyFields = (pdfForm: any) => {
  try {
    const fieldNames = pdfForm.getFields();
    for (const f of fieldNames) {
      let type = '';
      if (f instanceof PDFTextField) {
        type = 'PDFTextField';
      }
      if (f instanceof PDFDropdown) {
        type = 'PDFDropdown';
      }
      if (f instanceof PDFCheckBox) {
        type = 'PDFCheckBox';
      }
      const fieldName = f.getName();
      if (type === 'PDFTextField' || f instanceof PDFTextField) {
        pdfForm.getTextField(fieldName).enableReadOnly();
      }
      if (type === 'PDFDropdown' || f instanceof PDFDropdown) {
        pdfForm.getDropdown(fieldName).enableReadOnly();
      }
      if (type === 'PDFCheckBox' || f instanceof PDFCheckBox) {
        pdfForm.getCheckBox(fieldName).enableReadOnly();
      }
    }
  } catch (e) {
    console.log(e);
  }
};

const bufferToBase64 = (bufferData: { type: string; data: number[] }) => {
  const uint8Array = new Uint8Array(bufferData.data);
  return new TextDecoder().decode(uint8Array);
};

export async function fillIT2104Form(pdfUrl: string, formData: FormIT2104Data): Promise<Uint8Array | null> {
  try {
    const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const pdfForm = pdfDoc.getForm();
    readOnlyFields(pdfForm);
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const page = pdfDoc.getPages()[0];

    const fontSize = 8;
    const smallFontSize = 7;
    const checkboxMarkSize = 10;
    const black = rgb(2 / 255, 29 / 255, 149 / 255);

    drawText(page, formData.firstNameMI, 45, 685, font, fontSize, black);
    drawText(page, formData.lastName, 260, 685, font, fontSize, black);
    drawText(page, formData.socialSecurityNumber, 450, 685, font, fontSize, black);

    drawText(page, formData.homeAddress, 45, 662, font, fontSize, black);
    drawText(page, formData.apartmentNumber, 340, 662, font, fontSize, black);

    drawText(page, formData.cityVillagePO, 45, 640, font, fontSize, black);
    drawText(page, formData.state, 270, 640, font, fontSize, black);
    drawText(page, formData.zipCode, 365, 640, font, fontSize, black);

    if (formData.filingStatus === 'single') drawCheckboxMark(page, 515, 672, font, checkboxMarkSize, black);
    if (formData.filingStatus === 'married') drawCheckboxMark(page, 563, 672, font, checkboxMarkSize, black);
    if (formData.filingStatus === 'married_single_rate')
      drawCheckboxMark(page, 563, 660, font, checkboxMarkSize, black);

    if (formData.isNyCityResident === 'yes') drawCheckboxMark(page, 526, 625, font, checkboxMarkSize, black);
    if (formData.isNyCityResident === 'no') drawCheckboxMark(page, 566, 625, font, checkboxMarkSize, black);

    if (formData.isYonkersResident === 'yes') drawCheckboxMark(page, 526, 613, font, checkboxMarkSize, black);
    if (formData.isYonkersResident === 'no') drawCheckboxMark(page, 566, 613, font, checkboxMarkSize, black);

    drawText(page, formData.allowancesLine1, 515, 590, font, fontSize, black);
    drawText(page, formData.allowancesLine2, 515, 578, font, fontSize, black);

    drawText(page, formData.nysAmountLine3, 515, 541, font, fontSize, black);
    drawText(page, formData.nycAmountLine4, 515, 529, font, fontSize, black);
    drawText(page, formData.yonkersAmountLine5, 515, 517, font, fontSize, black);

    let employeeSignature: any = formData.employeeSignature || '';
    if (employeeSignature?.type === 'Buffer') {
      employeeSignature = bufferToBase64(employeeSignature);
    }
    if (employeeSignature?.startsWith('data:image/png;base64,')) {
      try {
        const pngImage = await pdfDoc.embedPng(employeeSignature);
        const sigBoxX = 20;
        const sigBoxY = 435;
        const sigBoxHeight = 35;
        const aspectRatio = pngImage.width / pngImage.height;
        let drawWidth = sigBoxHeight * aspectRatio;
        let drawHeight = sigBoxHeight;
        const maxWidth = 180;

        if (drawWidth > maxWidth) {
          drawWidth = maxWidth;
          drawHeight = maxWidth / aspectRatio;
        }
        const actualSigX = sigBoxX + (maxWidth - drawWidth) / 2;

        page.drawImage(pngImage, {
          x: actualSigX,
          y: sigBoxY + (sigBoxHeight - drawHeight) / 2,
          width: drawWidth,
          height: drawHeight,
        });
      } catch (sigError) {
        console.error('Error embedding signature image:', sigError);
      }
    } else if (formData.employeeSignature) {
      drawText(page, formData.employeeSignature, 45, 450, font, fontSize, black);
    }

    const signatureDateFormatted = formData.signatureDate ? moment(formData.signatureDate).format('MM/DD/YYYY') : '';
    drawText(page, signatureDateFormatted, 430, 447, font, fontSize, black);

    if (formData.claimedMoreThan14Exemptions) drawCheckboxMark(page, 395, 325, font, checkboxMarkSize, black);
    if (formData.isNewHireOrRehire) drawCheckboxMark(page, 184, 307, font, checkboxMarkSize, black);

    const firstDateServicesFormatted = formData.firstDateServicesForPay
      ? moment(formData.firstDateServicesForPay).format('MM/DD/YYYY')
      : '';
    drawText(page, firstDateServicesFormatted, 495, 307, font, smallFontSize, black);

    if (formData.dependentHealthBenefitsAvailable === 'yes')
      drawCheckboxMark(page, 389, 244, font, checkboxMarkSize, black);
    if (formData.dependentHealthBenefitsAvailable === 'no')
      drawCheckboxMark(page, 451, 244, font, checkboxMarkSize, black);

    if (formData.dependentHealthBenefitsAvailable === 'yes' && formData.dateEmployeeQualifiesForBenefits) {
      const dateQualifiesFormatted = moment(formData.dateEmployeeQualifiesForBenefits).format('MM/DD/YYYY');
      drawText(page, dateQualifiesFormatted, 304, 226, font, smallFontSize, black);
    }

    drawText(page, formData.employerNameAddress, 45, 180, font, smallFontSize, black);
    drawText(page, formData.employerIdNumber, 453, 180, font, smallFontSize, black);

    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  } catch (error) {
    console.error('Error filling IT-2104 PDF:', error);
    return null;
  }
}
